import { generatePath } from "react-router-dom";
import { startOfHour, endOfHour, differenceInSeconds } from "date-fns";
import _ from "lodash";
import { saveAs } from "file-saver";
import apiService from "../config/api-config";
import { getApplicationsTableList } from "./views/applications";
import timeFilterConfig from "../generics/config/time-filter-config";
import { getCookie, deleteCookie } from "../serviceWorker";
import {
  shortDisplayDate,
  displayUserExpChartTooltp,
  getStartOfHour,
  getEndOfHour,
  displayDateTime
} from "./displayTime";
import { getCurrentTheme } from "../common/Theme";
import { getThemeColors, meanQuality, statusColors } from "./colors"
import { BASE_V_MANAGE_URL } from "../apis/apiConstants";
import {
  PAGE_NAME,
  FORMAT_PAGE_NAME,
  FORMAT_CSV_PAGE_NAME,
  defaultCellValue,
  timeFilterTypes,
  timeFilterTypesCustomV4,
  timeFilters5m,
  timeMS,
  qoeFilterArray,
  routeURLs
} from "./enums";
import { tViews, tUtils, tConfig } from "../utils/table";
import JSZip from "jszip";

// Standard quality ranges across app
export const qoeRanges = {
  good: { min: 8, max: 10 },
  fair: { min: 5, max: 8 },
  poor: { min: 0, max: 5 }
};

/**
 * Get translated QoE label
 * @param {string} label - QoE label key (e.g., 'Good', 'Fair', 'Poor', 'gray', 'unknown')
 * @param {function} tCommon - Translation function for common namespace
 * @returns {string} Translated label
 */
export const getQoeLabel = (label, tCommon) => {
  if (!tCommon || typeof tCommon !== 'function') {
    return label;
  }
  const normalizedLabel = label?.toLowerCase();
  const labelMap = {
    'good': tCommon('appDashboardQOEGoodLabel'),
    'fair': tCommon('appDashboardQOEFairLabel'),
    'poor': tCommon('appDashboardQOEPoorLabel'),
    'unknown': tCommon('appDashboardQOEUnknownLabel'),
    'gray': tCommon('appQoeDashletGray')
  };
  return labelMap[normalizedLabel] || label;
};

const availabilityRanges = {
  good: { min: 95, max: 100 },
  fair: { min: 80, max: 95 },
  poor: { min: 0, max: 80 }
};

export const getCustomListApi = async (
  props,
  globalV4PayloadReport,
  isReporting
) => {
  const {
    setAggregateAppData,
    setAppsWithFamilySLA,
    globalFilter
  } = props;

  let promisesQueue = [];
  const defaultRequest = isReporting ? { ...globalV4PayloadReport } : { ...globalFilter.globalV4Payload };
  defaultRequest.sort = { usage: "desc" };
  /* This will call custom list api whenever we change site from site filter */
  promisesQueue.push(apiService.getTableCustomList(defaultRequest));

  try {
    const aggregateApplicationsResults = await Promise.all([...promisesQueue]);
    const aggregateApplicationsResult = aggregateApplicationsResults[0];

    setAggregateAppData(aggregateApplicationsResult);

    if (
      aggregateApplicationsResult?.errorObject ||
      !aggregateApplicationsResult?.data
    ) {
      setAppsWithFamilySLA([]);
      return;
    }

    let tableList = getApplicationsTableList(
      aggregateApplicationsResult.data
    );
    setAppsWithFamilySLA(tableList);
  } catch (error) {
    const errorResult = await createGenericErrorObject(error);
    setAggregateAppData(errorResult);
    setAppsWithFamilySLA([]);
  }
};

export const getSiteName = (type, siteInfo, selectedSiteId) => {
  let siteInfoData;
  // render sitename in pdf
  if (type === 'report') {
    siteInfoData = siteInfo.filter(a => selectedSiteId.includes(a.site_id)).map(a => a.site_name);
    return siteInfoData.toString().replace(/,/g, ", ");
  } else {
    // render sitename in report summary page
    siteInfoData = siteInfo.filter(a => selectedSiteId.includes(a.value)).map(a => a.text);
    return siteInfoData;
  }
}

export const getSiteId = (reportData) => {

  try {
    const _scope = JSON.parse(reportData?.scope);

    // Add safety check for missing site_id
    if (!_scope.site_id || !Array.isArray(_scope.site_id)) {
      return [];
    }

    // Handle the case where site_id is an array of strings that need to be parsed
    // Example: ["\"1\",\"100\",\"102\""] should become [1, 100, 102]
    if (_scope.site_id.length === 1 && _scope.site_id[0].includes(',')) {
      // Parse comma-separated values and clean quotes
      return _scope.site_id[0]
        .split(',')
        .map(id => id.trim().replace(/^"|"$/g, '')) // Remove leading/trailing quotes
        .filter(id => id !== '') // Remove empty strings
        .map(id => Number(id) ? Number(id) : id); // Convert to number if possible
    }

    // Handle normal array case
    return _scope.site_id
      .filter(id => id !== '') // Remove empty strings
      .map(id => Number(id) ? Number(id) : id); // Convert to number if possible
  } catch (error) {
    console.error('Error parsing scope in getSiteId:', error);
    return [];
  }
}

// Given a value, return its QOE score
export const getQoeScore = value => {
  return getScore(value, qoeRanges);
};

// Given a value, return its availability score
export const getAvailabilityScore = value => {
  return getScore(value, availabilityRanges);
};

const getScore = (value, ranges) => {
  //i.e. range: {poor: {min: 0, max: 5}fair: {min: 5, max: 8},good: {min: 8, max: 10}}
  for (const [quality, range] of Object.entries(ranges)) {
    if (value >= range.min && value <= range.max) {
      return quality;
    }
  }
  return null;
};

// Get formatted filter items
export const getFilterItems = items => {
  return Object.values(items).map(item => {
    const { label, value, additionalText } = item;
    return { label, value, additionalText };
  });
};

// Return true if current compare to previous props changed, otherwise false
export const hasFilterChanged = (curr, prev) => {
  // Compare current and previous time period arrays
  const selectedTime = curr.timePeriodSelected;
  const prevSelectedTime = prev.timePeriodSelected;
  const currentTimeStamp = curr.currentTimeStamp;
  const prevTimeStamp = prev.currentTimeStamp;

  //Compare current and previous applied sites
  const selectedSite = curr.selectedSite;
  const prevSelectedSite = prev.selectedSite;
  const selectedNhName = curr.selectedNhName || "";
  const prevSelectedNhName = prev.selectedNhName || "";
  // Compare current and previous selected overlay
  const currOverlay = curr.selectedOverlay;
  const prevOverlay = prev.selectedOverlay;
  const overlayChange = currOverlay !== prevOverlay;

  const timeFilterChange =
    prevSelectedTime &&
    !isCustom(selectedTime) &&
    selectedTime !== prevSelectedTime;
  const siteChange =
    selectedSite !== prevSelectedSite || selectedNhName !== prevSelectedNhName;
  const currentTimeStampChange =
    currentTimeStamp && currentTimeStamp !== prevTimeStamp;
  //Compute final result based on all props check
  const isChanged =
    overlayChange || timeFilterChange || siteChange || currentTimeStampChange;
  //Compare custom time filter dates
  if (isCustom(selectedTime) && !isChanged) {
    const _curr = curr.selectedCustomTime;
    const _prev = prev.selectedCustomTime ? prev.selectedCustomTime : prev;

    if (_.isEmpty(_curr)) return false;

    // Compare custom time filter dates by computing current_period[0] and current_period[1] for current and previous props
    const currCustomTimeCurrentPeriodZero = _curr && _curr.current_period[0];
    const prevCustomTimeCurrentPeriodZero = _prev.timeFilter
      ? _prev.timeFilter.current_period[0]
      : _prev.current_period[0];

    const currCustomTimeCurrentPeriodOne = _curr && _curr.current_period[1];
    const prevCustomTimeCurrentPeriodOne = _prev.timeFilter
      ? _prev.timeFilter.current_period[1]
      : _prev.current_period[1];

    const customTimeCurrentPeriodZero =
      currCustomTimeCurrentPeriodZero !== prevCustomTimeCurrentPeriodZero;
    const customTimeCurrentPeriodOne =
      currCustomTimeCurrentPeriodOne !== prevCustomTimeCurrentPeriodOne;

    //if custom time filter dates are different return true, otherwise false
    if (customTimeCurrentPeriodZero || customTimeCurrentPeriodOne) {
      return true;
    } else {
      return false;
    }
  }

  return isChanged;
};
export const refresh = async (props, pathname) => {

  let res = await apiService.getCsrfToken(getCookie("okta-oauth-state"));

  if (res && res.data && res.status === 200 && res.data["loginState"]) {
    if (pathname.includes("anomalydetection")) {
      props.setAnomalyRefresh(Math.random());
    } else if (!pathname.includes("accounts")) {
      const time = props.globalFilter.timePeriodSelected;

      if (props.sideBar) {
        props.setSideBarTimeFilter(
          {
            current_period: props.globalFilter.timeFilter.current_period,
            previous_period: props.globalFilter.timeFilter.previous_period
          },
          time
        );
      }
      props.setRefreshScreen();
    }
  } else {
    window.location.reload();
  }
};
export const isCustom = str => {
  return str === "Custom";
};

export const createObjFromEmailList = list => {
  const arrEmails = list?.split(",");
  const textfieldgroup =
    arrEmails &&
    arrEmails.map((email, key) => {
      return {
        name: "recipientEmail",
        validator: "email",
        validateon: "oninput",
        inputid: `recipientEmailId${key + 1}`,
        required: true,
        label: `${tUtils('commonUtilEmail')} ` + (key + 1),
        value: email,
        order: key,
        errormsg: ""
      };
    });
  return { textfieldgroup: textfieldgroup };
};

const isExecutiveSummary = location => {
  return location.pathname.includes("/executive_summary");
};

const isApplicationSummary = location => {
  return location.pathname.includes("/application_summary");
};

const isSiteSummary = location => location.pathname.includes("/site_summary");

const isLinkSLAReport = location => location.pathname.includes("/link_sla");

const isLinkUtilizationReport = location => location.pathname.includes("/link_utilization");

const isLinkAvailabilityReport = location => location.pathname.includes("/link_availability");

const isLinkSummaryReport = location => location.pathname.includes("/link_summary");


const isApp360Report = location => {
  return location.pathname.includes("/application_summary/");
};

export const isReport = location => {
  return location.pathname.includes("/report/");
};
export const isAccountsPage = () => {
  return location.pathname.includes(routeURLs.accounts);
};

export const isOverviewPage = () => {
  return location.pathname.includes(routeURLs.overview);
};

export const isAllSitesPage = () => {
  return location.pathname.includes(routeURLs.sites);
};

export const isAnomalyPage = () => {
  return location.pathname.includes(`/anomalydetection`);
};

export const isSecurityPage = () => {
  return location.pathname.includes(routeURLs.threatIntelligence);
};

export const isTrafficLogsPage = () => {
  return location.pathname.includes(routeURLs.trafficLogs);
};

export const isDeviceInfoPage = () => {
  return location.pathname.includes(routeURLs.appInfoPathMetric);
};

export const isPredictiveNetworksPage = () => {
  return location.pathname.includes(routeURLs.pn);
};

//In convergence UI, optimization, threat intelligence is a submenu under Analytics
export const isAnalyticsPage = () => {
  const isOptimization = isOptimizationPage();
  const isThreatIntelligence = isSecurityPage();
  const isPredictiveNetworks = isPredictiveNetworksPage();

  return isOptimization || isThreatIntelligence || isPredictiveNetworks;
};

export const isReportsPage = () => {
  return location.pathname.includes(routeURLs.reports);
};

export const isOptimizationPage = () => {
  return location.pathname.includes(routeURLs.optimization);
};

export const getTitleHeader = location => {

  switch (true) {
    case isExecutiveSummary(location):
      return tUtils('commonUtilExecutiveSummary');
    case isApp360Report(location):
      return tUtils('commonUtilTemplateSingleApp');
    case isApplicationSummary(location):
      return tUtils('commonUtilAllApplicationsReport');
    case isSiteSummary(location):
      return tUtils('commonUtilTemplatesSitesSummary');
    case isLinkSLA(location):
      return tUtils('commonUtilTemplatesSlaReport');
    case isLinkUtilization(location):
      return tUtils('commonUtilTemplatesLinkUtilizationReport');
    case isLinkAvailability(location):
      return tUtils('commonUtilTemplatesLinkAvailabilityReport');
    case isLinkSummary(location):
      return tUtils('commonUtilTemplatesLinkSummaryReport');
    case isTopClients(location):
      return tUtils('commonUtilTemplatesTopClients');
    default:
      return tUtils('commonUtilEditReport');
  }
};

export const titleStyle = location => {
  return isExecutiveSummary(location) ||
    isApplicationSummary(location) ||
    isSiteSummary(location) ||
    isLinkSLAReport(location) ||
    isLinkUtilizationReport(location) ||
    isLinkAvailabilityReport(location) ||
    isLinkSummaryReport(location) ||
    isTopClients(location)
    ? {
      position: "initial"
    }
    : {};
};

export const isDefaultHeader = location => {
  return (
    isExecutiveSummary(location) ||
    isApplicationSummary(location) ||
    isSiteSummary(location) ||
    isLinkSLAReport(location) ||
    isLinkUtilizationReport(location) ||
    isLinkAvailabilityReport(location) ||
    isLinkSummaryReport(location) ||
    isTopClients(location) ||
    isReport(location)
  );
};

export const isExecutiveTemplate = type => {
  return type === PAGE_NAME.EXECUTIVE || type === FORMAT_PAGE_NAME.EXECUTIVE;
};

export const isAllApplicationsTemplate = type => {
  return (
    type === PAGE_NAME.ALL_APPLICATIONS ||
    type === FORMAT_PAGE_NAME.ALL_APPLICATIONS
  );
};

export const isAllSitesTemplate = type => {
  return type === PAGE_NAME.ALL_SITES || type === FORMAT_PAGE_NAME.ALL_SITES;
};

export const isApp360 = type => {
  return type === PAGE_NAME.APP_360;
};

export const isAll = str => {
  return str === "all";
};

export const formatPageName = (type, fileType, scope) => {
  if (fileType === "pdf") {
    switch (true) {
      case isExecutiveTemplate(type):
        return FORMAT_PAGE_NAME.EXECUTIVE;
      case isAllApplicationsTemplate(type):
        return FORMAT_PAGE_NAME.ALL_APPLICATIONS;
      case isAllSitesTemplate(type):
        return FORMAT_PAGE_NAME.ALL_SITES;
      case isApp360(type):
        return FORMAT_PAGE_NAME.APP_360;
      case isLinkSLATemplate(type):
        return FORMAT_PAGE_NAME.LINK_SLA;
      case isLinkUtilizationTemplate(type):
        return FORMAT_PAGE_NAME.LINK_UTILIZATION;
      case isLinkAvailabilityTemplate(type):
        return FORMAT_PAGE_NAME.LINK_AVAILABILITY;
      case isTop100ClientsTemplate(type):
        return FORMAT_PAGE_NAME.TOP_CLIENTS;
      default:
        return FORMAT_PAGE_NAME.SITE_360;
    }
  } else if (fileType === "csv") {
    //TODO
    switch (true) {
      case (isAllApplicationsTemplate(type) && _.isEmpty(scope.application)) ||
        scope.application == "all":
        return FORMAT_CSV_PAGE_NAME.ALL_APPLICATIONS;
      case isAllApplicationsTemplate(type) && !_.isEmpty(scope.application):
        return FORMAT_CSV_PAGE_NAME.APP_360;
      case isAllSitesTemplate(type):
        return FORMAT_CSV_PAGE_NAME.ALL_SITES;
      case isLinkSLATemplate(type):
        return FORMAT_CSV_PAGE_NAME.LINK_SLA;
      case isLinkUtilizationTemplate(type):
        return FORMAT_CSV_PAGE_NAME.LINK_UTILIZATION;
      case isLinkAvailabilityTemplate(type):
        return FORMAT_CSV_PAGE_NAME.LINK_AVAILABILITY;
      case isLinkSummaryTemplate(type):
        return FORMAT_CSV_PAGE_NAME.LINK_SUMMARY;
      case isTop100ClientsTemplate(type):
        return FORMAT_CSV_PAGE_NAME.CLIENTS_SUMMARY;
    }
  }
};

export const formatScope = (scope, name = null) => {
  switch (true) {
    case isAllApplicationsTemplate(name) && !_.isEmpty(scope.application):
      return (
        `{` +
        '"' +
        `site_id` +
        '"' +
        `:[` +
        '"' +
        '"' +
        `],` +
        '"' +
        "application" +
        '"' +
        `:[` +
        '"' +
        `${scope.application}` +
        '"' +
        `]` +
        (!_.isEmpty(scope.business_hours) ? `,` +
          '"' +
          `business_hours` +
          '"' +
          `:{` +
          '"' +
          "start" +
          '"' +
          `:` +
          '"' +
          `${scope.business_hours.start}` +
          '"' +
          `,` +
          '"' +
          "end" +
          '"' +
          `:` +
          '"' +
          `${scope.business_hours.end}` +
          '"' +
          `}` : '') +
        `}`
      );
    case isAllApplicationsTemplate(name) && _.isEmpty(scope.application):
      return (
        `{` +
        '"' +
        `site_id` +
        '"' +
        `:[` +
        '"' +
        '"' +
        `],` +
        '"' +
        "application" +
        '"' +
        `:[` +
        '"' +
        `all` +
        '"' +
        `]` +
        (!_.isEmpty(scope.business_hours) ? `,` +
          '"' +
          `business_hours` +
          '"' +
          `:{` +
          '"' +
          "start" +
          '"' +
          `:` +
          '"' +
          `${scope.business_hours.start}` +
          '"' +
          `,` +
          '"' +
          "end" +
          '"' +
          `:` +
          '"' +
          `${scope.business_hours.end}` +
          '"' +
          `}` : '') +
        `}`
      );
    case isAllSitesTemplate(name) && !_.isEmpty(scope.site_id):
      return (
        `{` +
        '"' +
        `site_id` +
        '"' +
        `:[` +
        `${Array.isArray(scope.site_id) ? scope.site_id.map(id => `"${id}"`).join(',') : `"${scope.site_id}"`}` +
        `],` +
        '"' +
        "application" +
        '"' +
        `:[` +
        '"' +
        '"' +
        `]` +
        (!_.isEmpty(scope.business_hours) ? `,` +
          '"' +
          `business_hours` +
          '"' +
          `:{` +
          '"' +
          "start" +
          '"' +
          `:` +
          '"' +
          `${scope.business_hours.start}` +
          '"' +
          `,` +
          '"' +
          "end" +
          '"' +
          `:` +
          '"' +
          `${scope.business_hours.end}` +
          '"' +
          `}` : '') +
        `}`
      );
    case isAllSitesTemplate(name) && _.isEmpty(scope.site_id):
      return (
        `{` +
        '"' +
        `site_id` +
        '"' +
        `:[` +
        '"' +
        `all` +
        '"' +
        `],` +
        '"' +
        "application" +
        '"' +
        `:[` +
        '"' +
        '"' +
        `]` +
        (!_.isEmpty(scope.business_hours) ? `,` +
          '"' +
          `business_hours` +
          '"' +
          `:{` +
          '"' +
          "start" +
          '"' +
          `:` +
          '"' +
          `${scope.business_hours.start}` +
          '"' +
          `,` +
          '"' +
          "end" +
          '"' +
          `:` +
          '"' +
          `${scope.business_hours.end}` +
          '"' +
          `}` : '') +
        `}`
      );
    case isExecutiveTemplate(name):
      return (
        `{` +
        '"' +
        `site_id` +
        '"' +
        `:[` +
        '"' +
        '"' +
        `],` +
        '"' +
        "application" +
        '"' +
        `:[` +
        '"' +
        '"' +
        `]` +
        (!_.isEmpty(scope.business_hours) ? `,` +
          '"' +
          `business_hours` +
          '"' +
          `:{` +
          '"' +
          "start" +
          '"' +
          `:` +
          '"' +
          `${scope.business_hours.start}` +
          '"' +
          `,` +
          '"' +
          "end" +
          '"' +
          `:` +
          '"' +
          `${scope.business_hours.end}` +
          '"' +
          `}` : '') +
        `}`
      );
    case isLinkSLATemplate(name) && !_.isEmpty(scope.site_id):
      return (
        `{` +
        '"' +
        `site_id` +
        '"' +
        `:[` +
        `${Array.isArray(scope.site_id) ? scope.site_id.map(id => `"${id}"`).join(',') : `"${scope.site_id}"`}` +
        `],` +
        '"' +
        "application" +
        '"' +
        `:[` +
        '"' +
        '"' +
        `]` +
        (!_.isEmpty(scope.business_hours) ? `,` +
          '"' +
          `business_hours` +
          '"' +
          `:{` +
          '"' +
          "start" +
          '"' +
          `:` +
          '"' +
          `${scope.business_hours.start}` +
          '"' +
          `,` +
          '"' +
          "end" +
          '"' +
          `:` +
          '"' +
          `${scope.business_hours.end}` +
          '"' +
          `}` : '') +
        `}`
      );
    case isLinkSLATemplate(name) && _.isEmpty(scope.site_id):
      return (
        `{` +
        '"' +
        `site_id` +
        '"' +
        `:[` +
        '"' +
        `all` +
        '"' +
        `],` +
        '"' +
        "application" +
        '"' +
        `:[` +
        '"' +
        '"' +
        `]` +
        (!_.isEmpty(scope.business_hours) ? `,` +
          '"' +
          `business_hours` +
          '"' +
          `:{` +
          '"' +
          "start" +
          '"' +
          `:` +
          '"' +
          `${scope.business_hours.start}` +
          '"' +
          `,` +
          '"' +
          "end" +
          '"' +
          `:` +
          '"' +
          `${scope.business_hours.end}` +
          '"' +
          `}` : '') +
        `}`
      );
    case isLinkUtilizationTemplate(name) && !_.isEmpty(scope.site_id):
      return (
        `{` +
        '"' +
        `site_id` +
        '"' +
        `:[` +
        `${Array.isArray(scope.site_id) ? scope.site_id.map(id => `"${id}"`).join(',') : `"${scope.site_id}"`}` +
        `],` +
        '"' +
        "application" +
        '"' +
        `:[` +
        '"' +
        '"' +
        `]` +
        (!_.isEmpty(scope.business_hours) ? `,` +
          '"' +
          `business_hours` +
          '"' +
          `:{` +
          '"' +
          "start" +
          '"' +
          `:` +
          '"' +
          `${scope.business_hours.start}` +
          '"' +
          `,` +
          '"' +
          "end" +
          '"' +
          `:` +
          '"' +
          `${scope.business_hours.end}` +
          '"' +
          `}` : '') +
        `}`
      );
    case isLinkUtilizationTemplate(name) && _.isEmpty(scope.site_id):
      return (
        `{` +
        '"' +
        `site_id` +
        '"' +
        `:[` +
        '"' +
        `all` +
        '"' +
        `],` +
        '"' +
        "application" +
        '"' +
        `:[` +
        '"' +
        '"' +
        `]` +
        (!_.isEmpty(scope.business_hours) ? `,` +
          '"' +
          `business_hours` +
          '"' +
          `:{` +
          '"' +
          "start" +
          '"' +
          `:` +
          '"' +
          `${scope.business_hours.start}` +
          '"' +
          `,` +
          '"' +
          "end" +
          '"' +
          `:` +
          '"' +
          `${scope.business_hours.end}` +
          '"' +
          `}` : '') +
        `}`
      );
    case isLinkAvailabilityTemplate(name) && !_.isEmpty(scope.site_id):
      return (
        `{` +
        '"' +
        `site_id` +
        '"' +
        `:[` +
        `${Array.isArray(scope.site_id) ? scope.site_id.map(id => `"${id}"`).join(',') : `"${scope.site_id}"`}` +
        `],` +
        '"' +
        "application" +
        '"' +
        `:[` +
        '"' +
        '"' +
        `]` +
        (!_.isEmpty(scope.business_hours) ? `,` +
          '"' +
          `business_hours` +
          '"' +
          `:{` +
          '"' +
          "start" +
          '"' +
          `:` +
          '"' +
          `${scope.business_hours.start}` +
          '"' +
          `,` +
          '"' +
          "end" +
          '"' +
          `:` +
          '"' +
          `${scope.business_hours.end}` +
          '"' +
          `}` : '') +
        `}`
      );
    case isLinkAvailabilityTemplate(name) && _.isEmpty(scope.site_id):
      return (
        `{` +
        '"' +
        `site_id` +
        '"' +
        `:[` +
        '"' +
        `all` +
        '"' +
        `],` +
        '"' +
        "application" +
        '"' +
        `:[` +
        '"' +
        '"' +
        `]` +
        (!_.isEmpty(scope.business_hours) ? `,` +
          '"' +
          `business_hours` +
          '"' +
          `:{` +
          '"' +
          "start" +
          '"' +
          `:` +
          '"' +
          `${scope.business_hours.start}` +
          '"' +
          `,` +
          '"' +
          "end" +
          '"' +
          `:` +
          '"' +
          `${scope.business_hours.end}` +
          '"' +
          `}` : '') +
        `}`
      );
    case isLinkSummaryTemplate(name) && _.isEmpty(scope.site_id):
      return (
        `{` +
        '"' +
        `site_id` +
        '"' +
        `:[` +
        '"' +
        `all` +
        '"' +
        `],` +
        '"' +
        "application" +
        '"' +
        `:[` +
        '"' +
        '"' +
        `]` +
        (!_.isEmpty(scope.business_hours) ? `,` +
          '"' +
          `business_hours` +
          '"' +
          `:{` +
          '"' +
          "start" +
          '"' +
          `:` +
          '"' +
          `${scope.business_hours.start}` +
          '"' +
          `,` +
          '"' +
          "end" +
          '"' +
          `:` +
          '"' +
          `${scope.business_hours.end}` +
          '"' +
          `}` : '') +
        `}`
      );
    case isLinkSummaryTemplate(name) && !_.isEmpty(scope.site_id):
      return (
        `{` +
        '"' +
        `site_id` +
        '"' +
        `:[` +
        `${Array.isArray(scope.site_id) ? scope.site_id.map(id => `"${id}"`).join(',') : `"${scope.site_id}"`}` +
        `],` +
        '"' +
        "application" +
        '"' +
        `:[` +
        '"' +
        '"' +
        `]` +
        (!_.isEmpty(scope.business_hours) ? `,` +
          '"' +
          `business_hours` +
          '"' +
          `:{` +
          '"' +
          "start" +
          '"' +
          `:` +
          '"' +
          `${scope.business_hours.start}` +
          '"' +
          `,` +
          '"' +
          "end" +
          '"' +
          `:` +
          '"' +
          `${scope.business_hours.end}` +
          '"' +
          `}` : '') +
        `}`
      );
    case isTop100ClientsTemplate(name) && !_.isEmpty(scope.site_id):
      return (
        `{` +
        '"' +
        `site_id` +
        '"' +
        `:[` +
        `${Array.isArray(scope.site_id) ? scope.site_id.map(id => `"${id}"`).join(',') : `"${scope.site_id}"`}` +
        `],` +
        '"' +
        "application" +
        '"' +
        `:[` +
        '"' +
        '"' +
        `]}`
      );
    case isTop100ClientsTemplate(name) && _.isEmpty(scope.site_id):
      return (
        `{` +
        '"' +
        `site_id` +
        '"' +
        `:[` +
        '"' +
        `all` +
        '"' +
        `],` +
        '"' +
        "application" +
        '"' +
        `:[` +
        '"' +
        '"' +
        `]}`
      );
    default:
      return (
        `{` +
        '"' +
        `site_id` +
        '"' +
        `:[` +
        `${Array.isArray(scope.site_id) ? scope.site_id.map(id => `"${id}"`).join(',') : `"${scope.site_id}"`}` +
        `],` +
        '"' +
        "application" +
        '"' +
        `:[` +
        '"' +
        `${scope.application}` +
        '"' +
        `]` +
        (!_.isEmpty(scope.business_hours) ? `,` +
          '"' +
          `business_hours` +
          '"' +
          `:{` +
          '"' +
          "start" +
          '"' +
          `:` +
          '"' +
          `${scope.business_hours.start}` +
          '"' +
          `,` +
          '"' +
          "end" +
          '"' +
          `:` +
          '"' +
          `${scope.business_hours.end}` +
          '"' +
          `}` : '') +
        `}`
      );
  }
};

export const isRunNow = input => {
  return input === "once";
};

export const isFreqNow = input => {
  return input === "now";
};

export const isRecurring = input => {
  return input === "recurring";
};

export const is1Hour = input => {
  return input === "1";
};

export const is3Hours = input => {
  return input === "3";
};

export const is6Hours = input => {
  return input === "6";
};

export const isDaily = input => {
  return input === "daily" || input === "24";
};

export const isWeekly = input => {
  return input === "weekly" || input === "168";
};

export const isMonthly = input => {
  return input === "monthly" || input === "720";
};

export const getFreqMessage = input => {
  switch (true) {
    case isDaily(input):
      return tUtils('commonUtilDaily');
    case isWeekly(input):
      return tUtils('commonUtilLastWeek');
    case isMonthly(input):
      return tUtils('commonUtilLastMonth');
  }
};

export const getTimeFilter = input => {
  switch (true) {
    case is1Hour(input):
      return "1";
    case is3Hours(input):
      return "3";
    case is6Hours(input):
      return "6";
    case isDaily(input):
      return "24";
    case isWeekly(input):
      return "168";
    case isMonthly(input):
      return "720";
    default:
      return "12";
  }
};

export const getRepeatOn = day => {
  switch (true) {
    case day === "Monday":
      return 1;
    case day === "Tuesday":
      return 2;
    case day === "Wednesday":
      return 3;
    case day === "Thursday":
      return 4;
    case day === "Friday":
      return 5;
    case day === "Saturday":
      return 6;
    case day === "Sunday":
      return 0;
    default:
      return -1;
  }
};

export const getRepeatOnDay = repeat_on => {
  switch (repeat_on) {
    case 1:
      return "Monday";
    case 2:
      return "Tuesday";
    case 3:
      return "Wednesday";
    case 4:
      return "Thursday";
    case 5:
      return "Friday";
    case 6:
      return "Saturday";
    case 0:
      return "Sunday";
    default:
      return shortDisplayDate(new Date());
  }
};

export const convertHoursToFreq = h => {
  const days = Math.trunc(h / 24);
  switch (true) {
    case days === 1:
      return "daily";
    case days === 7:
      return "weekly";
    case days === 30:
      return "monthly";
    default:
      return "daily";
  }
};

export const isReportingApi = () => {
  return location.pathname.includes("/api/dataservice/reporting/");
};

export const isSortingAscending = type => {
  if (type === "asc") return true;
  else return false;
};

export const isReportsView = location => {
  return (
    location.pathname.includes("/report/") ||
    location.pathname.includes("/reporting/")
  );
};

export const isAdminRole = roles => {
  return Array.isArray(roles) && roles.length > 0 && roles.includes("admin");
};

export const sortedSitesList = sitesList => {
  return sitesList.sort((a, b) => {
    return parseInt(a.value) - parseInt(b.value);
  });
};

export const getRandomString = len => {
  const charset =
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  const charsetLen = charset.length;
  const values = new Uint8Array(len);
  // crypto.getRandomValues() generates cryptographically strong random values
  window.crypto.getRandomValues(values);
  const passKey = Array.from(values, value => charset[value % charsetLen]).join(
    ""
  );
  return passKey;
};

export const generateUID = length => {
  return window
    .btoa(
      Array.from(window.crypto.getRandomValues(new Uint8Array(length * 2)))
        .map(b => String.fromCharCode(b))
        .join("")
    )
    .replace(/[+/]/g, "")
    .substring(0, length);
};

export const timeUnits = [
  { name: "year", timestamp: 365 * 24 * 60 * 60 * 1000 },
  { name: "month", timestamp: 30 * 24 * 60 * 60 * 1000 },
  { name: "week", timestamp: 7 * 24 * 60 * 60 * 1000 },
  { name: "day", timestamp: 1 * 24 * 60 * 60 * 1000 },
  { name: "hour", timestamp: 1 * 60 * 60 * 1000 },
  { name: "minute", timestamp: 1 * 60 * 1000 },
  { name: "second", timestamp: 1 * 1000 }
];

export const getInterval = data => {
  let step = 0;
  data
    .sort((a, b) => b.date - a.date)
    .forEach((item, dex, self) => {
      if (dex < self.length - 1) {
        const newStep = item.date - self[dex + 1].date;
        if (step === 0) {
          step = newStep;
        } else {
          step = newStep < step ? newStep : step;
        }
      }
    });
  let timeUnit = timeUnits[5].name;
  let count = 1;

  if (step > timeUnits[0].timestamp) {
    timeUnit = timeUnits[0].name;
    count = Math.floor(step / timeUnits[0].timestamp);
  } else if (step > timeUnits[1].timestamp) {
    timeUnit = timeUnits[1].name;
    count = Math.floor(step / timeUnits[1].timestamp);
  } else if (step > timeUnits[2].timestamp) {
    timeUnit = timeUnits[2].name;
    count = Math.floor(step / timeUnits[2].timestamp);
  } else if (step > timeUnits[3].timestamp) {
    timeUnit = timeUnits[3].name;
    count = Math.floor(step / timeUnits[3].timestamp);
  } else if (step > timeUnits[4].timestamp) {
    timeUnit = timeUnits[4].name;
    count = Math.floor(step / timeUnits[4].timestamp);
  } else if (step > timeUnits[5].timestamp) {
    timeUnit = timeUnits[5].name;
    count = Math.floor(step / timeUnits[5].timestamp);
  }
  const result = { timeUnit, count };
  return result;
};

export const hasTimeFilterChanged = (curr, prev) => {
  return _.isEqual(curr, prev) ? false : true;
};

export const getLineChartDummyData = data => {
  let dummyData = [];
  let responseDate = data[0].date ? new Date(data[0].date) : new Date(data[0].entry_time);
  let roundDown = startOfHour(responseDate);
  let roundUp = endOfHour(responseDate);
  dummyData.push({
    date: roundDown,
    value: data[0].value ? data[0].value : data[0].vqoe_score
  });
  dummyData.push({
    date: roundUp,
    value: data[0].value ? data[0].value : data[0].vqoe_score
  });

  return dummyData;
};
export const setLocalStorageFlag = (key, val) => {
  localStorage.setItem(key, val);
};

export const getLocalStorageFlag = key => {
  return localStorage.getItem(key);
};

export const removeLocalStorageFlag = key => {
  return localStorage.removeItem(key);
};

export const removeItemsLocalStorage = () => {
  removeLocalStorageFlag("currentOverlay");
  removeLocalStorageFlag("currentOverlayName");
  removeLocalStorageFlag("onboardingStatus");
  removeLocalStorageFlag("roles");
  removeLocalStorageFlag("timePeriodSelected");
  removeLocalStorageFlag("currentSite");
};

export const arrayToObject = (array, attr) =>
  array.reduce((obj, item) => {
    obj[item[attr]] = item[attr];
    return obj;
  }, {});

export const arrayToObjectExtended = (array, attr) =>
  array.reduce((obj, item) => {
    obj[item[attr]] = item;
    return obj;
  }, {});

export const getTextValueTopSites = sitesView => {
  switch (true) {
    case sitesView === "availability":
      return {
        get viewBy() { return tUtils('commonUtilViewByWorst') },
        get secondColHeader() { return tUtils('commonUtilAvailability') },
        get thirdColHeader() { return tUtils('commonUtilUsageLabel') },
      };
    default:
      return {
        get viewBy() { return tUtils('commonUtilViewByTopRecom') },
        get secondColHeader() { return tUtils('commonUtilRecomPathQuality') },
        get thirdColHeader() { return tUtils('commonUtilState') },
      };
  }
};

export const isSiteViewedByAvailability = input => {
  return input === "availability";
};

export const getTipGanttChart = (
  data,
  isDynamicDate = true,
  isOptionalElementShown
) => {
  const currentPathsColor = isDynamicDate
    ? data.currentPathsColor
    : `{currentPathsColor}`;
  const label = isDynamicDate ? data.label : `{label}`;
  const meanQ = isDynamicDate ? data.meanQuality : `{meanQuality}`;
  const showMeanElement = isOptionalElementShown
    ? `<table>
        <tr>
          <td id=${"td-row-mean-color"}>${meanQ}</td>
          <td id=${"td-row-mean-label"}>${tUtils('commonUtilappGroupPerSite.meanQualityTipLabel')
    }</td>
        </tr>
      </table>
      <div class=table-divider></div>`
    : ``;
  const showDatesElement = isOptionalElementShown
    ? `<div class=table-divider></div>
      <table class=table-bottom>
        <tr>
          <td>${tUtils('commonUtilappGroupPerSite.userExpChartFromLabel')
    } ${isDynamicDate ? data.fromDateFormated : ""
    } {fromDateFormated}</td>
          <td class=table-bottom>${tUtils('commonUtilappGroupPerSite.userExpChartToLabel')
    } ${isDynamicDate ? data.toDateFormated : ""}{toDateFormated}</td>
        </tr>
      </table>`
    : ``;

  return `
    ${showMeanElement}
    <table id=${"loadBalaceTooltipData"}>
      <tr>
        <td id=${"loadBalaceTooltipSingle"}>
          <span id=${"currentColor"} style="background:${currentPathsColor}">
          </span>
        </td>
        <td>
          <span id=${"loadBalaceTooltipPathName"}>${label}
          </span>
        </td>
        <td></td>
        <td></td>
      </tr>
    </table>
    <div class=table-divider></div>
    <table>
      <tr id=${"tooltip-title-row"}>
        <th>${tUtils('commonUtilappGroupPerSite.currentTipLabel')}</th>
        <td></td>
        <th>${tUtils('commonUtilappGroupPerSite.recommendedTipLabel')
    }</th>
        <td>
        </td>
      </tr>
      ${Array(data.lengthOfPaths)
      .fill()
      .map(
        (pathElement, pathIndex) => `
              <tr id=${"loadBalaceTooltipData"}>
                <td tabindex="0" id=${"loadBalaceTooltip"}>
                  <span id=${"loadBalaceTooltipColorMark"}
                    style="background: ${data &&
          data.currentPathsColors[pathIndex]}">
                  </span>
                </td>

                <td>
                  <span id=${"loadBalaceTooltipPathName"}>
                  ${data && data.currentPaths[pathIndex]}
                  </span>
                </td>

                <td tabindex="1" id=${"loadBalaceTooltipSingle"}>
                  <span id=${"loadBalaceTooltipColorMark"}
                    style="background:${data
            ? data.recommendedPaths[pathIndex] &&
            data.recommendedPaths[pathIndex].color
            : "none"
          }">
                  </span>
                </td>

                <td>
                 <span id=${"loadBalaceTooltipPathName"}>${data &&
            data.recommendedPaths[pathIndex] &&
            data.recommendedPaths[pathIndex].pathName
            ? data.recommendedPaths[pathIndex].pathName
            : ``
          }
                  </span>
                </td>
              </tr>
      `
      )
      .join("")}
    </table>
    ${showDatesElement}
  `;
};

export const getTipBulletColumnChart = (data = "") => {
  return `
    <table class=table-top>
      <tr>
        <td id=${"td-row-1"}>${data &&
    data.recommendationQuality}{recommendationQuality}% </td>
        <td id=${"td-row-2"}>${tUtils('commonUtilappGroupPerSite.userExpChartRecomLabel')
    }</td>
      </tr>
      <tr>
        <td id=${"td-row-1"}>${data &&
    data.defaultQuality}{defaultQuality}%</td>
        <td id=${"td-row-2"}>${tUtils('commonUtilappGroupPerSite.userExpChartCurrentPathLabel')
    }</td>
      </tr>
    </table>
    <div class=table-divider></div>
    <table class=table-bottom>
      <tr>
        <td id=${"td-row-num"}>${data &&
    data.totalNumUsers} {totalNumUsers}</td>
        <td>${tUtils('commonUtilappGroupPerSite.userExpChartActiveUsrLabel')
    }</td>
      </tr>
    </table>
    <div class=table-divider></div>
    <table class=table-bottom>
      <tr>
        <td id=${"date-row"}>${tUtils('commonUtilappGroupPerSite.userExpChartFromLabel')
    }
        ${data && data.fromDateFormated} {fromDateFormated}</td>
        <td id=${"date-second-col"}>${tUtils('commonUtilappGroupPerSite.userExpChartToLabel')
    }
        ${data && data.toDateFormated} {toDateFormated}</td>
      </tr>
    </table>`;
};

export const getTooltipMissingData = data => {
  return `
    <table id=${"no-data-tooltip"}>
      <tr>
        <td id=${"td-row-no-data"}>${tUtils('commonUtilMissingData')
    }</td>
      </tr>
      <tr>
        <td id=${"td-row-no-data"}>${tUtils('commonUtilLackTelemetryData')
    }</td>
      </tr>
      <tr>
        <td id=${"td-row-no-data"}>${tUtils('commonUtilStagnation')
    }</td>
      </tr>
    </table>
    <div class=table-divider></div>
    <table>
      <tr>
        <td id=${"no-data-from-line"}>${data}</td>
      </tr>
    </table>`;
};

export const getTooltipNoData = data => {
  return `
    <table id=${"no-data-tooltip"}>
      <tr>
        <td id=${"td-row-no-data"}>${tUtils('commonUtilMissingData')
    }</td>
      </tr>
      <tr>
        <td id=${"td-row-no-data"}>${tUtils('commonUtilLackTelemetry')
    }</td>
      </tr>
      <tr>
        <td id=${"td-row-no-data"}>${tUtils('commonUtilStagnation')
    }</td>
      </tr>
      <tr>
        <td id=${"td-row-no-data"}></td>
      </tr>
      <tr>
        <td id=${"td-row-no-data"}></td>
      </tr>
    </table>
    <div class=table-divider></div>
    <table>
      <tr>
        <td id=${"no-data-from"}>${displayUserExpChartTooltp(
      data.fromDate
    )}</td>
        <td id=${"no-data-to"}>${displayUserExpChartTooltp(data.toDate)}</td>
      </tr>
      <tr>
        <td id=${"no-data-from"}> ${""}</td>
        <td id=${"no-data-to"}>${""}</td>
      </tr>
    </table>
    <table>
      <tr>
        <td id=${"td-row-no-data-bottom"}>${tUtils('commonUtilAlldataMissing')
    }</td>
      </tr>
    </table>`;
};

export const getChartHight = size => {
  switch (true) {
    case size === 1:
      return 160;
    case size === 2:
      return 190;
    case size === 3:
      return 210;
    case size === 4:
      return 280;
    case size === 5:
      return 310;
    default:
      return 350;
  }
};

export const getColorFromMeanQuality = mean => {
  const meanQColorList = meanQuality;
  switch (true) {
    case parseInt(mean) > 94:
      return statusColors.good;
    case parseInt(mean) > 90:
      return meanQColorList[0];
    case parseInt(mean) > 80:
      return meanQColorList[1];
    case parseInt(mean) > 70:
      return meanQColorList[2];
    case parseInt(mean) > 60:
      return meanQColorList[3];
    case parseInt(mean) > 50:
      return meanQColorList[4];
    case parseInt(mean) > 40:
      return meanQColorList[5];
    case parseInt(mean) > 30:
      return meanQColorList[6];
    default:
      return meanQColorList[7];
  }
};

export const getMissingKeys = (current, next) => {
  let missingKeys = current.filter(ckey => {
    if (
      ckey !== "date" &&
      !next.some(nkey => ckey.toString() === nkey.toString())
    ) {
      return ckey;
    }
  });
  return missingKeys;
};

export const compareAndMatchCircuits = (usage, ciruitData) => {
  usage.sort((a, b) => (a.date > b.date ? 1 : -1));
  if (usage && usage.length > 0 && ciruitData && ciruitData.length > 0) {
    for (var i = 0; i < usage.length; i++) {
      if (
        usage[i] &&
        usage[i + 1] &&
        !usage[i].message &&
        !usage[i + 1].message
      ) {
        let missingKeys = getMissingKeys(
          Object.keys(usage[i]),
          Object.keys(usage[i + 1])
        );
        if (
          (usage[i].date.getHours() + 1 !== usage[i + 1].date.getHours() &&
            !(
              usage[i].date.getHours() === 23 &&
              usage[i + 1].date.getHours() === 0
            )) ||
          (usage[i].date.getHours() + 1 === usage[i + 1].date.getHours() &&
            usage[i].date.getDate() !== usage[i + 1].date.getDate())
        ) {
          let newEntry = { ...usage[i] };
          newEntry.date = new Date(newEntry.date.getTime() + 3599999);
          newEntry.message = "Entry";
          usage.push(newEntry);
        } else if (missingKeys.length > 0) {
          let missingEntry = { ...usage[i] };
          missingEntry.date = new Date(missingEntry.date.getTime() + 3599999);
          missingEntry.message = "Entry";
          missingKeys.map(mkey => {
            missingEntry[mkey] = usage[i][mkey];
          });
          usage.push(missingEntry);
        }
      }
    }
  }
  // Adding last toDate entry
  usage.sort((a, b) => (a.date > b.date ? 1 : -1));
  ciruitData.sort((a, b) => (a.fromDate > b.fromDate ? 1 : -1));
  let entry = { ...usage[usage.length - 1] };
  if (ciruitData[ciruitData.length - 1]) {
    entry.date = new Date(ciruitData[ciruitData.length - 1].toDate);
    entry.message = "Entry";
    usage.push(entry);
  }
  return usage;
};

export const compareAndMatch = (Data, ciruitData) => {
  if (ciruitData && ciruitData.length > 0 && Data && Data.length > 0) {
    ciruitData.sort((a, b) => (a.fromDate > b.fromDate ? 1 : -1));
    let start_point = ciruitData[0].fromDate;
    let end_point = ciruitData[ciruitData.length - 1].toDate;
    if (new Date(start_point) < new Date(Data[0].utcDate)) {
      Data.unshift({
        date: new Date(start_point),
        value: Data[0].value
      });
    }
    if (new Date(Data[Data.length - 1].utcDate) < new Date(end_point)) {
      Data.push({
        date: new Date(end_point),
        value: Data[Data.length - 1].value
      });
    }
    return Data;
  }
};

export const customizeByTimePeriod = (
  timePeriodSelected,
  twelveAndtwentyFour,
  sevenDays,
  rest
) => {
  let val = twelveAndtwentyFour;
  if (timePeriodSelected === "12" || timePeriodSelected === "24") {
    val = twelveAndtwentyFour;
  } else if (timePeriodSelected === "168") {
    val = sevenDays;
  } else {
    val = rest;
  }
  return val;
};

export const sortdata = (requestParam, sortedData) => {
  if (Object.keys(requestParam).length > 0 && sortedData.length > 0) {
    const { property, order } = requestParam.sort[0];
    const [first] = sortedData;
    const sortvaluetype = typeof first[property];
    return sortedData.sort((firstElement, secondElement) => {
      switch (sortvaluetype) {
        case "string": {
          //This is for String IP address sorting
          if (property === "user_ip") {
            const num1 = Number(
              firstElement[property]
                .split(".")
                .map(num => `000${num}`.slice(-3))
                .join("")
            );
            const num2 = Number(
              secondElement[property]
                .split(".")
                .map(num => `000${num}`.slice(-3))
                .join("")
            );
            return order === "asc" ? num1 - num2 : num2 - num1;
          } else {
            return order === "asc"
              ? firstElement[property] > secondElement[property]
                ? 1
                : -1
              : firstElement[property] < secondElement[property]
                ? 1
                : -1;
          }
        }
        case "number":
          return order === "desc"
            ? secondElement[property] - firstElement[property]
            : firstElement[property] - secondElement[property];
      }
    });
  } else {
    return sortedData;
  }
};

/**
 * description: This method creates a header for the reporting templates
 * with the overlay name and type of the template
 * @param {*} overlayName is a response from api that need to be displayed in the report header
 * @param {*} location is used to identify and display the template name ex: executive summary / application summary/ all sites summary
 */
export const getReportsHeader = (overlayName, location) => {
  const isDarkTheme = getCurrentTheme() === "dark";
  const headerClassName = `app-header-workflow${isDarkTheme ? " report-dark-mode" : ""}`;

  return (
    <>
      <div className={headerClassName} style={titleStyle(location)}>
        <div className="mega-workflow">
          <div className="app-name">
            <div className="header-brand">
              <hbr-icon size="32px" name="cisco"></hbr-icon>
            </div>
            <div className="product-name">Catalyst SD-WAN</div>
          </div>
        </div>
        <div className="title-report-left">
          {overlayName !== "" ? overlayName : null}
        </div>
        <div className="title-report">
          <div>{getTitleHeader(location)}</div>
        </div>
      </div>
    </>
  );
};

/**
 * Description: This method creates a generic error object
 * @param {*} response is a response from api with the error config object
 * @param {*} message is the error message
 */

export const createGenericErrorObject = async ({ response, message }) => {
  let errorObject = { errorCode: null, message: "" };
  let streamText = "";

  if (response) {
    errorObject.errorCode = response.status || "500";

    if (response.data) {
      // Handle ReadableStream explicitly first
      if (response.data instanceof ReadableStream) {
        const reader = response.data.getReader();
        const decoder = new TextDecoder("utf-8");
        let done = false;

        // Read the stream's content
        while (!done) {
          const { value, done: streamDone } = await reader.read();
          done = streamDone;
          if (value) streamText += decoder.decode(value, { stream: true });
        }

        // Ensure the errorCode reflects the status_code for a ReadableStream
        errorObject.errorCode = response.status;

        try {
          // Attempt to parse as JSON
          const parsedData = JSON.parse(streamText);
          if ("error" in parsedData) {
            errorObject.message = parsedData.error;
          } else if ("error_code" in parsedData) {
            errorObject.message = parsedData.message;
          } else {
            errorObject.message = streamText; // Fallback to raw text
          }
        } catch (e) {
          errorObject.message = streamText; // Fallback to raw text if JSON parsing fails
        }
      }

      // Handle string data
      else if (typeof response.data === "string") {
        errorObject.message = response.data;
      }

      // Handle object data
      else if (typeof response.data === "object") {
        if ("error" in response.data) {
          errorObject.message = response.data.error;
        } else if ("TAC_flag" in response.data) {
          errorObject = response.data;
        } else if ("error_code" in response.data) {
          errorObject.errorCode = response.data.error_code;
          errorObject.message = response.data.message;
        }
      }
    }
  }

  // Ensure the final message prioritizes the resolved 'streamText' if available
  if (!errorObject.message && streamText) {
    // Assign the raw streamText as the message if none was set
    errorObject.message = streamText;
  } else if (!errorObject.message) {
    // Fallback to provided message
    errorObject.message = message;
  }

  return { errorObject };
};

/**
 * Description: validate if the list of sites contain any site with valid latitude and longitude
 * @param {*} sites is an array that contains a list of sites objets
 * each object contains details about site like latidute and longitude
 */
export const validateSitesLocation = sites => {
  let isUnknownSiteLocation = true;

  for (const siteIndex of sites) {
    if (
      (siteIndex.latitude == "0" && siteIndex.longitude == "0") ||
      siteIndex.latitude < "-65" ||
      siteIndex.latitude > "65"
    ) {
      isUnknownSiteLocation = false;
      break;
    }
  }
  return isUnknownSiteLocation;
};

/**
 *
 * Description: set the currentpath in the localstorage on page refresh
 *
 */
export const setCurrentPath = () => {
  switch (true) {
    case pathValidation(tConfig('navConfigOverview')):
      setActiveItem(tConfig('navConfigOverview'));
      break;
    case pathValidation(tConfig('navConfigSites')):
      setActiveItem(tConfig('navConfigSites'));
      break;
    case pathValidation(tConfig('navConfigApplications')) ||
      pathValidation(tConfig('navConfigDashboard')):
      setActiveItem(tConfig('navConfigApplications'));
      break;
    case pathValidation(tConfig('navConfigCircuits')):
      setActiveItem(tConfig('navConfigCircuits'));
      break;
    case pathValidation(tConfig('navConfigRecommendations')) ||
      pathValidation(tConfig('navConfigPredictiveNetworks')):
      setActiveItem(tConfig('navConfigPredictiveNetworks'));
      break;
    case pathValidation(tConfig('navConfigReportsLabel')):
      setActiveItem(tConfig('navConfigReportsLabel'));
      break;
    default:
      setActiveItem(tConfig('navConfigOverview'));
      break;
  }
};

/**
 * Description: Validate the location pathname
 * @param {*} label is a string that contains the navigation label
 *
 */
export const pathValidation = label => {
  if (label) {
    let lbl = label.toLowerCase();
    let loc = location.pathname.toLowerCase();
    return loc.includes(lbl);
  }
};

/**
 * Description: set localstorage value for currentPath
 * @param {*} val is a string that contains the current active navigation label
 *
 */
export const setActiveItem = val => {
  localStorage.setItem("currentPath", val);
};

/**
 * Description: set innerHTML in the browser DOM using dangerouslySetInnerHTML
 * @param {*} strHTML is a string with HTML content
 *
 */
export const createMarkup = strHTML => {
  return { __html: strHTML };
};

// customizeGrip for scroll bar
export const customizeGrip = (grip, am4core) => {
  grip.icon.disabled = true;
  grip.background.disabled = true;
  let line = grip.createChild(am4core.Rectangle);
  line.height = 80;
  line.width = 5;
  line.fillOpacity = 0.4;
  line.align = "center";
  line.valign = "middle";
};

/**
 * Description: Checks conditions to display comparison text in time filter section
 * @param {*} location is a react router's location object
 *
 */

export const showComparisonText = location => {
  if (location.pathname.startsWith(routeURLs.circuits) || location.pathname.startsWith(routeURLs.threatIntelligence)) return false;
  return true;
};

export const isBandForecastingTabSelected = () => {
  return (
    location.pathname === routeURLs.bwf
  );
};

/**
 *
 * @param {*} data that is being sorted
 * @param {*} actualData is the original data that we feed the table
 * @param {*} settings
 * @param {*} columns
 * sort the table asc or desc
 */
export const tableSorting = (data, actualData, settings, columns) => {
  // Calculate the start and end indexes based on the scroll position
  let startIndex = data.start;
  let endIndex = data.start + data.length;
  let searchValue = settings.oPreviousSearch.sSearch;
  let filteredData = actualData.filter(function (row) {
    let matchesSearch = false;
    for (let i = 0; i < settings.aoColumns.length; i++) {
      let column = settings.aoColumns[i];
      if (
        column.bSearchable &&
        row[column.data]
          .toString()
          .toLowerCase()
          .indexOf(searchValue.toLowerCase()) !== -1
      ) {
        matchesSearch = true;
        break;
      }
    }
    return matchesSearch;
  });

  // sort the filtered data based on the sorting column and direction
  let sortColumn = columns[data.order[0].column];
  if (Number.isFinite(sortColumn.orderData))
    sortColumn = columns[sortColumn.orderData];
  const columnName = sortColumn.data;
  if (sortColumn && columnName) {
    const sortDirection = data.order[0].dir;
    if (sortColumn.type === "num") {
      if (sortDirection === "asc")
        filteredData.sort((item1, item2) =>
          item1[columnName] === defaultCellValue
            ? -1
            : item2[columnName] === defaultCellValue
              ? 1
              : item1[columnName] - item2[columnName]
        );
      else
        filteredData.sort((item1, item2) =>
          item2[columnName] === defaultCellValue
            ? -1
            : item1[columnName] === defaultCellValue
              ? 1
              : item2[columnName] - item1[columnName]
        );
    } else {
      if (sortDirection === "asc")
        filteredData.sort((item1, item2) =>
          item1[columnName] < item2[columnName] ? -1 : 1
        );
      else
        filteredData.sort((item1, item2) =>
          item2[columnName] < item1[columnName] ? -1 : 1
        );
    }
  }

  // Slice the data array based on the start and end indexes
  let newData = filteredData.slice(startIndex, endIndex);
  return { newData, filteredCount: filteredData.length };
};

//To get post-login notifications
export const getPostLoginNotifications = async setPostLoginNotifications => {
  if (!isReportsView(location)) {
    try {
      let res = await apiService.getPostLoginNotifications();
      if (res && res.data && res.status === 200) {
        setPostLoginNotifications(res.data.data);
      }
    } catch (err) {
      console.log("Error while calling getPostLoginNotifications API", err);
      createGenericErrorObject(err);
    }
  }
};

//To get report timerange text using time_range response
export const getReportTrendText = data => {
  switch (true) {
    case data === "daily":
      return " previous day";
    case data === "weekly":
      return " previous week";
    case data === "monthly":
      return " previous month";
    default:
      return " previous day";
  }
};

//Get time frame input in terms of days to send payload in report site api call
export const getReportTimeRangeDate = data => {
  switch (true) {
    case data === "daily":
      return "24h";
    case data === "weekly":
      return "7d";
    case data === "monthly":
      return "30d";
    default:
      return "24h";
  }
};

//Get time period in hours
export const getReportTimePeriod = data => {
  switch (true) {
    case data === "daily":
      return "24";
    case data === "weekly":
      return "168";
    case data === "monthly":
      return "720";
    default:
      return "24";
  }
};

//Get time period in hours
export const getReportTimeFrame = data => {
  switch (true) {
    case data === "24h":
      return "24";
    case data === "7d":
      return "168";
    case data === "30d":
      return "720";
    default:
      return "24";
  }
};

// This method scrolls to the top of the page
export const scrollToTop = () => {
  if (typeof document.documentElement.scrollTo === "function")
    document.documentElement.scrollTo({ top: 0, left: 0, behavior: "instant" });
  else document.documentElement.scrollTop = 0;
};

//when circuit name is clicked redirect to circuit 360
export const circuitNameClick = (navigate, rowData) => {
  if (rowData.circuit_display_name) {
    localStorage.setItem(
      "circuit",
      JSON.stringify({
        ...rowData,
        overlay: localStorage.getItem("currentOverlay")
      }));
    const circuitName = rowData.circuit_display_name.split(":").join("~");
    navigate(generatePath(routeURLs.circuit360, { circuitName }));
  }
};

export const enableTunnelSelection = tunnelInfo => {
  let enableSelection =
    tunnelInfo.tunnel_name &&
    !tunnelInfo.tunnel_name.toUpperCase()?.includes("DIA") &&
    !tunnelInfo.tunnel_name.toUpperCase()?.includes("SIG") &&
    tunnelInfo.vqoe_score !== "-";
  return enableSelection;
};

/**
 * Description: This method returns info icon with tooltip
 * @param {string} content is the tooltip text
 */
export const getInfoIcon = content => `
  <hbr-tooltip hoist content="${content}">
    <hbr-icon-button  name="info"  sentiment="neutral"></hbr-icon-button>
  </hbr-tooltip>
`;

// Format legend data
export const formatLegendData = (legend, timeFilterType) => {
  const { theme, labels } = legend;
  const _colors = getThemeColors(theme);
  return labels.map((label, index) => {
    const color = _colors[index];
    const formatLabel =
      label === tUtils('commonUtilPrevious') && timeFilterConfig[timeFilterType]
        ? `${tUtils('commonUtilPrevious')} ${timeFilterConfig[timeFilterType].legendText.toLowerCase()}`
        : label;
    return { label: formatLabel, color };
  });
};

/// date in timestamp
export const compareAndMatchELT = (data, timeFilter, entry_time = "date") => {
  if (data && data.length > 0) {
    let lastRecord = data[data.length - 1];
    let matchedData = {}

    if (
      lastRecord[entry_time] < timeFilter.current_period[1] &&
      lastRecord[entry_time] ==
      getStartOfHour(timeFilter.current_period[1].valueOf())
    ) {
      Object.keys(lastRecord).forEach((key) => {
        matchedData[key] = key == entry_time ? timeFilter.current_period[1] : lastRecord[key]
      })
      if (lastRecord.tooltipData) {
        matchedData["tooltipData"][entry_time] = displayDateTime(matchedData[entry_time])
      }
      data.push(matchedData)
    } else {
      if (
        lastRecord[entry_time] < timeFilter.current_period[1] &&
        lastRecord[entry_time] !=
        timeFilter.current_period[1] &&
        getEndOfHour(lastRecord[entry_time]).valueOf() != lastRecord[entry_time]
      ) {
        Object.keys(lastRecord).forEach((key) => {
          matchedData[key] = key == entry_time ? new Date(getEndOfHour(lastRecord[key])).valueOf() : lastRecord[key]
        })
        if (lastRecord.tooltipData) {
          matchedData["tooltipData"][entry_time] = displayDateTime(matchedData[entry_time])
        }
        data.push(matchedData)
      }
    }
  }
  return data;
};

/// date in UTC
export const compareAndMatchCircuitsELT = (data, timeFilter, entry_time) => {
  if (data && data.length > 0) {
    let lastRecord = data[data.length - 1];
    let matchedData = {}
    let endTime = timeFilter.current_period ? timeFilter.current_period[1] : timeFilter[1]
    if (
      lastRecord[entry_time] < timeFilter.current_period[1] &&
      lastRecord[entry_time].valueOf() ==
      getStartOfHour(endTime.valueOf())
    ) {
      Object.keys(lastRecord).forEach((key) => {
        matchedData[key] = key == entry_time ? endTime : lastRecord[key]
      })
      data.push(matchedData)
    } else {
      if (
        lastRecord[entry_time] < timeFilter.current_period[1] &&
        lastRecord[entry_time] !=
        endTime &&
        getEndOfHour(lastRecord[entry_time]).valueOf() != lastRecord[entry_time]
      ) {
        Object.keys(lastRecord).forEach((key) => {
          matchedData[key] = key == entry_time ? getEndOfHour(lastRecord[key]).valueOf() : lastRecord[key]
        })
        data.push(matchedData)
      } else {
        if (
          lastRecord[entry_time].valueOf() > timeFilter.current_period[1]
        ) {
          lastRecord[entry_time] = timeFilter.current_period[1];
        }
      }
    }
  }
  return data;
};

export const compareAndMatchDateTime = (data, globalFilter, entry_time) => {
  if (data && data.length > 0) {
    let lastRecord = data[data.length - 1];
    let matchedData = {}
    let diffSeconds = differenceInSeconds(
    new Date(globalFilter.timeFilter.current_period[1]),
    new Date(lastRecord[entry_time])
    );
    if (
      diffSeconds > 0 &&
      getStartOfHour(lastRecord[entry_time]).valueOf() ==
      getStartOfHour(globalFilter.timeFilter.current_period[1]).valueOf()
    ) {

      Object.keys(lastRecord).forEach((key) => {
        matchedData[key] = key == entry_time ? globalFilter.timeFilter.current_period[1] : JSON.parse(JSON.stringify(lastRecord[key]))
      })
      matchedData["tooltipData"][entry_time] = displayDateTime(new Date(globalFilter.timeFilter.current_period[1]).valueOf())
      data.push(matchedData)
    } else {
      if (
        diffSeconds > 0 &&
        lastRecord[entry_time] !=
        globalFilter.timeFilter.current_period[1] &&
        getEndOfHour(lastRecord[entry_time]).valueOf() != lastRecord[entry_time]
      ) {
        Object.keys(lastRecord).forEach((key) => {
          matchedData[key] = key == entry_time ? new Date(getEndOfHour(lastRecord[key])).valueOf() : JSON.parse(JSON.stringify(lastRecord[key]));
        })
        matchedData["tooltipData"][entry_time] = displayDateTime(matchedData[entry_time])
        data.push(matchedData)
      }
    }
  }
  return data;
};

/**
 *
 * @param {*} data  is an object with data and returns qoe status count
 */

export const getMapSummaryApp360 = (props) => {
  const isSitesView = ['availability', 'recommendation'].includes(props?.sitesView);
  const hasSites = Array.isArray(props?.sites) && props.sites.length > 0;
  const isReportingPage = props?.isReporting;

  let data = [];
  // sites page QOE widget and app360 QOE report
  if ((isReportingPage || isSitesView) && hasSites) {
    data = props.sites;
  }
  // app360 page QOE widget
  else if (props?.length > 0) {
    data = props;
  }
  if (!data || data.length === 0) {
    return {
      high: { count: 0 },
      fair: { count: 0 },
      low: { count: 0 },
      gray: { count: 0 },
      total_sites: 0
    };
  }

  const counts = qoeFilterArray.reduce((acc, status) => {
    acc[status] = data.filter(item => item.vqoe_status === status).length;
    return acc;
  }, {});

  return {
    high: { count: counts.good || 0 },
    fair: { count: counts.fair || 0 },
    low: { count: counts.poor || 0 },
    gray: { count: counts.unknown || 0 },
    total_sites: data.length
  };
}

/**
 *
 * @param {*} globalFilter
 * This function validate if custom filter has start and end date
 * and if there is none than return fixed time range like 24h or 7d value
 */
export const validateTimePeriodSelected = (globalFilter) => {

  //set default time period to 12h when user is on accounts page
  if (isAccountsPage()) return "12h";

  let isCustomRangeValid = false;

  if (globalFilter.selectedCustomTime?.range?.start && globalFilter.selectedCustomTime?.range?.end) {
    isCustomRangeValid = !isCustomRangeValid;
  }

  const storedTimePeriod = getStoredTimePeriod();
  return timeFilterTypes[isCustomRangeValid ? globalFilter.timePeriodSelected : storedTimePeriod];
}

export const validateTimePeriodSelectedSidebar = (globalFilter) => {
  let isCustomRangeValid = false;
  if (globalFilter.sideBarTimeFilter.current_period[0] && globalFilter.sideBarTimeFilter.current_period[1]) {
    isCustomRangeValid = !isCustomRangeValid;
  }
  const storedTimePeriod = getLocalStorageFlag("timePeriodSelected");
  return timeFilterTypes[isCustomRangeValid ? globalFilter.sideBarTimeFilter.timePeriodSelected : storedTimePeriod];
}

/**
 * Description: Searches the entry data at a given time
 * @param {object[]} arr is the chart data
 * @param {number} start is the start index
 * @param {number} end is the end index
 * @param {number} timestamp is the time to find the entry
 * @returns entry data at a given time
 */

export const binarySearchChartData = (
  arr,
  start,
  end,
  timestamp,
  interval = 3600000
) => {
  const diff = end - start;
  let mid = start + Math.floor(diff / 2);
  if (arr[mid].date === timestamp) return arr[mid];
  else if (diff === 1) {
    if (arr[end].date === timestamp) return arr[end];
    else {
      if (
        timestamp < arr[end].date
        && timestamp > arr[start].date
        && arr[end].date <= arr[start].date + interval
      )
        return arr[start];
      return null;
    }
  }
  else if (arr[mid].date > timestamp)
    return binarySearchChartData(arr, start, mid, timestamp, interval);
  if (arr[mid].date < timestamp)
    return binarySearchChartData(arr, mid, end, timestamp, interval);
};

// To generate random UUID for Geo Map sites
export const randomUUID = () => {
  return 'xxxxxxxx-xxxx-xxxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/**
 * get the time period from local storage
 */
export const getStoredTimePeriod = () => {
  const isUIconvergence = getCookie("cl-analytics");
  const isOptimization = isOptimizationPage();
  //Optimization page default time period is 12
  //UI convergence default time period is 1
  //Other pages default time period is 12
  let defaultTimePeriodSelected = isUIconvergence && isOptimization === false ? "1" : "12";

  const vMangeRedirectURI = getCookie("cl-vManageURI");

  if (vMangeRedirectURI && isUIconvergence) {
    const analyticsCookies = getCookie("cl-analytics") ?
      JSON.parse(getCookie("cl-analytics")) : {};
    const timePeriod = analyticsCookies.timePeriodSelected ?
      analyticsCookies.timePeriodSelected
      : "1";
    defaultTimePeriodSelected = timePeriod;
  } else {
    if (Number.isInteger(parseInt(getLocalStorageFlag("timePeriodSelected")))) {
      defaultTimePeriodSelected = getLocalStorageFlag("timePeriodSelected")
    }
  }
  return defaultTimePeriodSelected;
}

/**
 * get the time period from local storage
 */
export const getStoredPath = () => {
  let currentPath = "overview";

  const vMangeRedirectURI = getCookie("cl-vManageURI");
  const launchCookie = getCookie("launch-dashboard");
  const isUIconvergence = getCookie("cl-analytics");

  if (vMangeRedirectURI && isUIconvergence) {
    const analyticsCookies = JSON.parse(getCookie("cl-analytics"));
    currentPath = analyticsCookies.currentPath ?
      analyticsCookies.currentPath.toLowerCase()
      : "overview";
  } else if (launchCookie) {
    currentPath = launchCookie.toLowerCase();
  } else {
    currentPath = getLocalStorageFlag("currentPath").toLowerCase();
  }

  //take care of the use case where path is i.e. /v4/sites
  if (currentPath.includes("/")) {
    const slash = currentPath.split("/");
    currentPath = slash[slash.length - 1]
  }

  return currentPath;
}

/**
 * get current site from local storage
 */
export const getStoredCurrentSite = () => {
  let site = -1;

  const isUIconvergence = getCookie("cl-analytics");

  if (isUIconvergence) {
    const analyticsCookies = getCookie("cl-analytics") ?
      JSON.parse(getCookie("cl-analytics")) : {};
    const currentSite = parseInt(analyticsCookies.currentSite);

    site = Number.isInteger(currentSite) ?
      currentSite : null;

  } else {
    const currentSite = parseInt(getLocalStorageFlag("currentSite"));
    site = Number.isInteger(currentSite) ?
      currentSite :
      -1
  }
  return site;
}

/**
 * get the mode value for tab navigation from local storage
 */
export const getConvergedCookie = (type) => {
  let cookie = "";

  const isCookiePresent = getCookie("cl-analytics");

  if (isCookiePresent) {
    const analyticsCookies = getCookie("cl-analytics") ?
      JSON.parse(getCookie("cl-analytics")) : {};
    cookie = analyticsCookies[type];
  }

  return cookie;
}

/**
 * get the fabric mode value for tab navigation from local storage
 */
export const getStoredTabFabricMode = () => {
  let isNonFabricMode = false;

  const isUIconvergence = getCookie("cl-analytics");

  if (isUIconvergence) {
    const analyticsCookies = getCookie("cl-analytics") ?
      JSON.parse(getCookie("cl-analytics")) : {};
    isNonFabricMode = analyticsCookies.isNonFabricMode === true;
  }

  return isNonFabricMode;
}

/**
 * updated the cl-analytics cookie with new value and key
 */
export const updateCookie = (cookieKey, cookieValue) => {
  const isUIconvergence = getCookie("cl-analytics");
  const originalCookie = {
    "timePeriodSelected": "1",
    "currentSite": null,
    "mode": "singleTenant",
    "isNonFabricMode": false,
    "tenancyMode": "",
    "tenantId": ""
  };

  if (isUIconvergence) {
    if (getCookie("cl-analytics")) {
      const originalCookie = JSON.parse(getCookie("cl-analytics"));
      originalCookie[cookieKey] = cookieValue;
      deleteCookie("cl-analytics")
      document.cookie = `cl-analytics=${JSON.stringify(originalCookie)}; path=/; SameSite=None; Secure`;
    } else {
      deleteCookie("cl-analytics")
      originalCookie[cookieKey] = cookieValue;
      document.cookie = `cl-analytics=${JSON.stringify(originalCookie)}; path=/; SameSite=None; Secure`;
    }
  }
}

export const updateClCookie = (cookieKey, cookieValue) => {
  // Update the cl-selected-nh-name cookie with the selected non-site hierarchy label name
  if (cookieValue === null || cookieValue === "" || cookieValue === undefined) {
    deleteCookie(cookieKey);
  } else {
    deleteCookie(cookieKey);
    document.cookie = `${cookieKey}=${encodeURIComponent(cookieValue)}; path=/; SameSite=None; Secure`;
  }
};

//key and value style
export const getKeyValueStyle = (key, value) => {
  return (
    <div className="key-value">
      <span className="key">{key}</span>
      <span className="value">{value ? value : defaultCellValue}</span>
    </div>
  )
}
export const validateTimePeriodSelectedSidebarPayload = (globalFilter) => {
  let isCustomRangeValid = false;
  if (globalFilter.time_period.current_period[0] && globalFilter.time_period.current_period[1]) {
    isCustomRangeValid = !isCustomRangeValid;
  }
  const storedTimePeriod = getLocalStorageFlag("timePeriodSelected");
  return timeFilterTypesCustomV4[
    isCustomRangeValid ? globalFilter.timePeriodSelected : storedTimePeriod
  ];
}

/**
 * Description: Creates a CSV file with the given CSV content
 * @param {string} csvContent is the content of the CSV file
 * @param {string} title is the main part of the CSV file name
 */
export const downloadAsCSVFile = (csvContent, title = "Download", timestamp) => {
  const fileTimestamp = timestamp
    ? displayDateTime(timestamp, "yyyy-MM-dd hh mm ss aa")
    : displayDateTime(new Date(), "yyyy-MM-dd hh mm ss aa");
  const fileName = `${title}_${fileTimestamp}.csv`;
  const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8" });
  saveAs(blob, fileName);
};

/**
 * Description: If field contains comma, quote, or newline, wrap in quotes and escape quotes
 * @param {string} field is the content of the CSV file
 */
export const escapeCSVField = (field) => {
  if (field == null) return '""';
  const str = String(field);
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return `"${str}"`;
};


/**
 * Returns the name of the currently selected site from the global filter.
 * Falls back to the selectedSite value if the site is not found or sites array is missing.
 *
 * @param {Object} globalFilter - The global filter object containing sites and selectedSite.
 * @returns {string|number|undefined} The site name if found, otherwise the selectedSite value.
 */
export const getCurrentSelectedSiteName = (globalFilter) => {
  if (!globalFilter) return;

  const selectedSiteId = globalFilter.selectedSite;

  const sites = globalFilter.sites?.length
    ? globalFilter.sites
    : globalFilter.siteAPIData?.sitesTableData?.data || [];


  if (globalFilter.selectedSite == "-1") {
    // If "-1" is selected, return "All Sites"
    return PAGE_NAME.ALL_SITES;
  } else {
    // If no sites are available, return the selectedSiteId directly
    const site = sites.find(
      s => String(s.site_id) === String(selectedSiteId) || String(s.value) === String(selectedSiteId)
    );
    return site?.site_name || site?.text || selectedSiteId;
  }
};

/**
 * Description: map the icons from api with existing icons in harbor library
 * @param: icon is a string name
 */
export const getIconName = (icon) => {
  switch (true) {
    case icon === "computer": return "nav-monitor";
    case icon === "settings": return "nav-configuration";
    case icon === "build": return "nav-tools";
    case icon === "business_center": return "nav-maintenance";
    case icon === "people": return "nav-admin";
    case icon === "workflows": return "nav-workflows";
    case icon === "reports": return "nav-reports";
    case icon === "insert_chart": return "nav-insight";
    case icon === "explore": return "nav-explore";
    default: return icon;
  }
}

export const getTimeInterval = (timeFrame) => {
  return timeFilters5m.includes(timeFrame) ? timeMS.min5 : timeMS.hour1;
};

export const getIsHourlyData = (timeFrame) => {
  return !timeFilters5m.includes(timeFrame);
}

export const getTrendInitialLoad = (globalFilter, initialLoad) => {
  return ((initialLoad && globalFilter.sideBarTimeFilter.timePeriodSelected == globalFilter.timePeriodSelected) ||
    !initialLoad || globalFilter.sideBarTimeFilter.timePeriodSelected === undefined)
};

export const isAnalytics = () => {
  const isManager = getCookie("cl-vManageURI");
  const isConvergence = getCookie("cl-analytics");
  return !isManager && !isConvergence ? true : false;
};

export const kebabToTitleCase = (key) => {
  if (key.includes("severity")) return tUtils('commonUtilPriority');
  if (key.includes("policy")) return tUtils('commonUtilPolicyName');
  if (key.includes("malname")) return tUtils('commonUtilMalwareName');
  if (key.includes("url")) return "URL";

  return key.replace(/-/g, ' ').replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
};

export const isLinkSLATemplate = type => {
  return type === PAGE_NAME.LINK_SLA || type === FORMAT_PAGE_NAME.LINK_SLA;
};

export const isLinkUtilizationTemplate = type => {
  return type === PAGE_NAME.LINK_UTILIZATION || type === FORMAT_PAGE_NAME.LINK_UTILIZATION;
};

export const isLinkAvailabilityTemplate = type => {
  return type === PAGE_NAME.LINK_AVAILABILITY || type === FORMAT_PAGE_NAME.LINK_AVAILABILITY;
};

export const isLinkSummaryTemplate = type => {
  return type === PAGE_NAME.LINK_SUMMARY;
};

const isLinkSLA = location => location.pathname.includes("/link_sla");
const isLinkUtilization = location => location.pathname.includes("/link_utilization");
const isLinkAvailability = location => location.pathname.includes("/link_availability");
const isLinkSummary = location => location.pathname.includes("/link_summary");

const isTopClients = location => location.pathname.includes("/clients_summary");


export const isArrayEqual = (arr1, arr2) => {
  return Array.isArray(arr1) && Array.isArray(arr2) && arr1.length === arr2.length && arr1.every((val, index) => val === arr2[index]);
}

/**
   * Formats network data by removing jitter, latency, and loss_percentage keys
   * if their values are strings containing a hyphen.
   *
   * @param {Array<Object>} data - Network data to be formatted
   * @returns {Array<Object>} Formatted network data
   */
export const formatNetworkData = (data) => {
  const keysToRemove = ['jitter', 'latency', 'loss_percentage', 'vqoe_score'];

  return data.map(obj => {
    return Object.fromEntries(
      Object.entries(obj).filter(([key, value]) =>
        !(keysToRemove.includes(key) && typeof value === 'string' && value.includes('-'))
      )
    );
  });
};

/**
 * Reloads the Qualtrics Site Intercept (QSI) by calling the appropriate API methods.
 *
 * This function checks if the QSI API is available on the 'window' object, and if so, performs the following steps in order:
 *   1. Unloads any currently loaded intercept.
 *   2. Loads the intercept configuration.
 *   3. Runs the intercept logic.
 *
 * If the QSI API is not available, a warning is logged to the console.
 */

export const reloadQSI = () => {
  if (window.QSI && window.QSI.API) {
    window.QSI.API.unload();
    window.QSI.API.load();
    window.QSI.API.run();
  } else {
    console.warn("QSI API is not available.");
  }
};

/**
 * Filters time filter options based on the current page context.
 *
 * @param {Object} filterOptions - The available time filter options (keyed by option name).
 * @param {Object} context - Flags indicating the current page context.
 *   @param {boolean} context.isSecurity - True if on the Security page.
 *   @param {boolean} context.isOptimization - True if on the Optimization page.
 *   @param {boolean} context.isPathAnalytics - True if on the Path Analytics page.
 * @returns {Object} The filtered time filter options.
 */
export const filterTimeOptions = (
  filterOptions,
  { isSecurity, isOptimization, isPathAnalytics }
) => {
  // Define which values to hide for each context
  const hideForSecurity = ['Custom'];
  const hideForOptimization = ['Custom', '1', '3', '6'];
  const hideForPathAnalytics = ['Custom'];

  // Determine which values to hide based on context
  let valuesToHide = [];
  if (isSecurity) {
    valuesToHide = hideForSecurity;
  }
  if (isOptimization) {
    valuesToHide = hideForOptimization;
  }
  if (isPathAnalytics) {
    valuesToHide = hideForPathAnalytics;
  }

  // Filter out options whose value is in the valuesToHide array
  const filteredOptions = {};
  for (const [key, value] of Object.entries(filterOptions)) {
    if (!valuesToHide.includes(value.value)) {
      filteredOptions[key] = value;
    }
  }
  return filteredOptions;
};

/**
 * Filters the selected devices from the list of all devices.
 * @param {*} allDevices - The list of all devices.
 * @param {*} selectedDevices - The list of selected devices.
 * @returns The filtered list of selected devices.
 */
export const filterSelectedDevices = (allDevices, selectedDevices) => {
  const filteredDevices = allDevices
    ?.filter((device) => selectedDevices.includes(device.text)) || [];
  return filteredDevices;
}

/**
 * Builds a map of device IDs from the list of all devices based on the selected devices.
 * @param {*} allDevices - The list of all devices.
 * @param {*} selectedDevices - The list of selected devices.
 * @returns A map of device IDs.
 */
export const buildDeviceIdsMap = (allDevices, selectedDevices) => {
  return allDevices
    ?.filter((device) => selectedDevices.includes(device.text))
    .map((device) => device.value) || [];
}

/**
 * Extracts and returns a key part of the current URL path.
 * - If the path has 4 segments (e.g., /analytics/v4/sites), returns the last segment (e.g., "sites").
 * - If the path has 5 segments (e.g., /analytics/v4/Dashboards/share-point), returns the last two segments joined by a slash (e.g., "Dashboards/share-point").
 * @param {object} location - The React Router location object.
 * @returns {string|undefined} The extracted path segment(s) or undefined if not matched.
 */
export const getCurrentPathName = location => {
  const path = location.pathname.split("/");
  if (path.length === 4) {
    return location.pathname.split("/")[3];
  }
  else if (path.length === 5) {
    const path = location.pathname.split("/")[3] + "/" + location.pathname.split("/")[4];
    return path;
  }
  else if (path.length === 6 && path.includes("predictivenetworks")) {
    const path = location.pathname.split("/")[3] + "/" + location.pathname.split("/")[5];
    return path;
  }
};

/**
 * Formats a given path name string for display or internal use.
 * - If isHashedPath is true, combines and capitalizes parts of a kebab-case string (e.g., "foo-bar" → "fooBar").
 * - If the path contains "/", splits and returns an array of segments.
 * - If the path contains "-", capitalizes the first letter of the suffix and joins (e.g., "foo-bar" → "fooBar").
 * - Otherwise, returns the path name as is.
 * @param {string} pathName - The path name to format.
 * @param {boolean} [isHashedPath=false] - Whether to treat the path as a hashed path.
 * @returns {string|string[]} The formatted path name or array of segments.
 */
export const formatPathName = (pathName, isHashedPath = false) => {
  if (!pathName) return "";
  if (isHashedPath) {
    const hashPath = pathName.split("-")[0] + pathName.split("-")[1].charAt(0).toUpperCase() + pathName.split("-")[1].slice(1);
    const formattedHashPath = hashPath.replace("-", "");
    return formattedHashPath;
  }
  else {
    if (pathName.includes("/")) {
      const paths = pathName.split("/");
      return paths
    } else {
      if (pathName.includes("-")) {
        const suffix = pathName.split("-")[1][0].toUpperCase() + pathName.split("-")[1].slice(1);
        return pathName.split("-")[0] + suffix;
      } else {
        return pathName;
      }
    }
  }
}

/**
 * Formats and returns a subtitle label based on the provided subtitle and title.
 * - For "Dashboards" and "circuits" titles, capitalizes the first letter of each word in a kebab-case subtitle (e.g., "foo-bar" → "Foo-Bar").
 * - For "Dashboards", if subtitle is not kebab-case, capitalizes the first letter.
 * - For "circuits", if subtitle is not kebab-case, returns as is.
 * - For "report", returns an empty string.
 * - For "predictivenetworks", returns a localized subtitle prefix.
 * - For "optimization", returns a localized app QoE details label.
 * @param {string} subTitle - The subtitle string to format.
 * @param {string} title - The main title to determine formatting rules.
 * @returns {string} The formatted subtitle label.
 */
export const getSubTitleLabel = (subTitle, title) => {
  if (!subTitle) return "";
  if (title === "Dashboards") {
    if (subTitle.includes("-")) {
      const formattedSubTitle = subTitle.split("-").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join("-");
      return formattedSubTitle;
    } else {
      const formattedSubTitle = subTitle.charAt(0).toUpperCase() + subTitle.slice(1);
      return formattedSubTitle;
    }
  }
  if (title === "circuits") {
    if (subTitle.includes("-")) {
      const formattedSubTitle = subTitle.split("-").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join("-");
      return formattedSubTitle;
    } else {
      return subTitle;
    }
  }
  if (title === "report") return "";
  if (title === "predictivenetworks") {
    const PNSubTitle = {
      "anomalydetection" : `${tViews('a11yLabels.subTitleAnomalyDetection')}`,
      "bandwidthforecasting" : `${tViews('pwBandwidthForecasting')}`,
      "site" : `${tViews('a11yLabels.subTitleSiteInformation')}`
    }
    return subTitle ? PNSubTitle[subTitle] : "";
  }
  if (title === "optimization") return `${tViews('a11yLabels.appQoeDetailsLabel')}`;
}

/**
 * Returns the page title label based on the provided value key.
 * - Maps known page keys (e.g., "accounts", "sites", "applications", "Dashboards", etc.) to their corresponding localized title labels.
 * - If the value matches multiple keys that share the same label (e.g., "applications" and "Dashboards"), both return the same label.
 * - Returns undefined if the value does not match any known key.
 * @param {string} value - The key representing the page or section.
 * @returns {string|undefined} The localized page title label, or undefined if not found.
 */
export const getTitleLabel = (value) => {
  const getPageTitleValue = {
    get accounts() { return tViews('a11yLabels.accountPageTitleLabel') },
    get trafficLogs() { return tViews('a11yLabels.logsPageTitleLabel') },
    get sites() { return tViews('a11yLabels.sitesPageTitleLabel') },
    get applications() { return tViews('a11yLabels.applicationsPageTitleLabel') },
    get Dashboards() { return tViews('a11yLabels.applicationsPageTitleLabel') },
    get circuits() { return tViews('a11yLabels.circuitsPageTitleLabel') },
    get generatedReports() { return `${tViews('a11yLabels.reportsPageTitleLabel')} - ${tViews('a11yLabels.generatedReportsPageTitleLabel')}` },
    get reportTemplates() { return `${tViews('a11yLabels.reportsPageTitleLabel')} - ${tViews('a11yLabels.reportTemplatePageTitleLabel')}` },
    get report() { return tViews('a11yLabels.generateReportPageTitleLabel') },
    get overview() { return tViews('a11yLabels.overviewPageTitleLabel') },
    get predictivenetworks() { return tViews('a11yLabels.predictiveNetworksPageTitleLabel') },
    get threatintelligence() { return tViews('a11yLabels.threatIntelligencePageTitleLabel') },
    get optimization() { return tViews('a11yLabels.optimizationPageTitleLabel') },
  };
  return value ? getPageTitleValue[value] : undefined;
}

/**
 * Sets the document page title based on the provided title and optional subtitle.
 * - If only the title is provided, sets the title as: "{title} | {allPageTitleLabel}".
 * - If both title and subtitle are provided, sets the title as: "{title} - {subTitle} | {allPageTitleLabel}".
 * - If neither is provided, sets the title as: "{allPageTitleLabel}".
 * @param {string} title - The main page title.
 * @param {string} [subTitle=""] - The optional subtitle to include.
 */
export const setPageTitle = (title, subTitle = "") => {
  if (subTitle.length === 0) {
    document.title = title ? `${title} | ${tViews('a11yLabels.allPageTitleLabel')}` : `${tViews('a11yLabels.allPageTitleLabel')}`;
  } else {
    document.title = title ? `${title} - ${subTitle} | ${tViews('a11yLabels.allPageTitleLabel')}` : `${tViews('a11yLabels.allPageTitleLabel')}`;
  }
}

/**
 * Unified navigation function that handles redirection based on tenancy mode and tenant ID
 * @param {Object|string} target - The navigation target (path object or string)
 * @param {string} _cookie - The cookie string to parse
 * @param {string} type - The type of navigation: 'tab', 'menu', or 'singleMenu'
 */
export const handleNavigation = (target, _cookie = getCookie("cl-analytics"), type = 'tab') => {
  const cookie = JSON.parse(_cookie) || {};

  if (cookie.tenancyMode === "tenant" && !_.isEmpty(cookie.tenantId)) {
    let redirectUrl = "/";

    switch (type) {
      case 'tab':
        redirectUrl = `/#/${cookie.tenantId}/app/monitor2${target.url}`;
        break;

      case 'menu':
        redirectUrl = `${target.slice(0, 3)}${cookie.tenantId}/${target.slice(3)}`;
        break;

      case 'singleMenu':
        redirectUrl = `/#/${cookie.tenantId}/${target?.sref.replaceAll(".", "/")}`;
        break;

      default:
        break;
    }

    window.location.href = redirectUrl;
  } else {
    // Non-tenant navigation
    switch (type) {
      case 'tab':
        window.location.href = `${BASE_V_MANAGE_URL}${target.url}`;
        break;

      case 'menu':
        window.location.href = target;
        break;

      case 'singleMenu':
        window.location.href = `/#/${target?.sref.replaceAll(".", "/")}`;
        break;

      default:
        break;
    }
  }
}

// Keep original functions for backward compatibility
export const onTabClick = (path, _cookie) => {
  handleNavigation(path, _cookie, 'tab');
}

export const onMenuClick = (path, _cookie) => {
  handleNavigation(path, _cookie, 'menu');
}

export const onSingleMenuClick = (path, _cookie) => {
  handleNavigation(path, _cookie, 'singleMenu');
}

export const isTop100ClientsTemplate = type => {
  return type === PAGE_NAME.TOP_CLIENTS || type === FORMAT_PAGE_NAME.TOP_CLIENTS;
};
export const isBusinessHourShown = type => {
  // Check if conditional_filters exists and has business_hours
  if (type.conditional_filters?.business_hours && Array.isArray(type.conditional_filters.business_hours)) {
    // Check if any condition matches the current file type
    return type.conditional_filters.business_hours.some(condition =>
      condition.file_type?.includes(type.fileType) && condition.time_range === timeFilterTypesCustomV4[type.timeRange]
    );
  }
  return false;
};


export const nonBusinessHourShown = type => {
  return (type.businessHours === undefined
    || (typeof type.businessHours === "object" && Object.keys(type.businessHours).length === 0))
}

/**
 * Builds render attributes for a percentage gain cell.
 *
 * @param {number|string} gainValue - Gain value to display as a percentage.
 * @param {string} positiveColor - Text color used when the gain is positive.
 * @param {string} negativeColor - Text color used when the gain is negative.
 * @param {string} placeholder - Text shown when the gain is missing or invalid.
 * @returns {{innerHTML?: string, style: Object}} Cell display text and inline style object.
 */
export const getPercentGainCellRenderAttrs = (props, positiveColor = "", negativeColor = "", placeholder = "--") => {
  if (["defaultQuality", "recommendationQuality"].includes(props?.prop)) {
    return { style: { textAlign: "right" } };
  }
  const gainValue = props?.model?.gains;
  const hasValue = gainValue !== null && gainValue !== undefined && gainValue !== "";
  const gain = Number(gainValue);

  if (!hasValue || Number.isNaN(gain)) {
    return { innerHTML: placeholder, style: {} };
  }

  const style = { textAlign: "right" };

  if (gain > 0 && positiveColor) {
    style.color = positiveColor;
  } else if (gain < 0 && negativeColor) {
    style.color = negativeColor;
  }

  return {
    innerHTML: `${gain > 0 ? "+" : ""}${gain}%`,
    style
  };
}

/**
 * Parse version string to extract major.minor as a number.
 *
 * @param {string} versionString - Version string like "26.2.0-37".
 * @returns {number} Parsed version as float (for example, 26.2) or 0 if invalid.
 */
export const parseVersion = (versionString) => {
  if (!versionString || versionString === "NA") return 0;
  const match = versionString.match(/^(\d+\.\d+)/);
  return match ? parseFloat(match[1]) : 0;
};

/**
 * Converts file definitions into CSV files, zips them, and triggers a download.
 *
 * @param {Object} params - Export options.
 * @param {Array<Object>} params.files - CSV files to include in the ZIP.
 * @param {string} params.files[].name - CSV file name.
 * @param {Array<string>} params.files[].headers - CSV column headers.
 * @param {Array<Array<*>>} params.files[].rows - CSV row values.
 * @param {string} [params.zipName="export.zip"] - Downloaded ZIP file name.
 * @returns {Promise<void>} Resolves after the ZIP download is triggered.
 */
export const exportCsvZip = async ({ files, zipName = "export.zip" }) => {
  const quote = value => `"${String(value == null ? "" : value).replace(/"/g, '""')}"`;

  const toCsv = (headers, rows) => {
    const delimiter = ",";
    return [headers.map(quote).join(delimiter)]
      .concat(rows.map(row => row.map(quote).join(delimiter)))
      .join("\n");
  };

  const zip = new JSZip();

  files.forEach(({ name, headers, rows }) => {
    zip.file(name, "\uFEFF" + toCsv(headers, rows));
  });

  const blob = await zip.generateAsync({ type: "blob" });
  saveAs(blob, zipName);
};
